import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', description);
  }, [title, description]);
}

function TopoBg() {
  return (
    <div className="topo-bg" aria-hidden="true">
      <svg className="topo-svg" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg" fill="none">
        <path d="M 0 400 Q 150 350 300 380 Q 450 410 600 370 Q 750 330 900 360 Q 1050 390 1200 350" stroke="currentColor" strokeWidth="1.5" color="#F5C84A" />
        <path d="M 0 350 Q 200 300 400 330 Q 600 360 800 310 Q 1000 260 1200 300" stroke="currentColor" strokeWidth="1" color="#F5C84A" />
        <path d="M 0 300 Q 180 250 360 280 Q 540 310 720 260 Q 900 210 1080 250 Q 1140 265 1200 255" stroke="currentColor" strokeWidth="0.75" color="#F5C84A" />
        <path d="M 0 450 Q 200 420 400 440 Q 600 460 800 430 Q 1000 400 1200 420" stroke="currentColor" strokeWidth="1" color="#F5C84A" />
        <path d="M 0 500 Q 250 480 500 495 Q 750 510 1000 485 Q 1100 478 1200 470" stroke="currentColor" strokeWidth="0.75" color="#F5C84A" />
        <path d="M 0 250 Q 300 200 600 225 Q 900 250 1200 210" stroke="currentColor" strokeWidth="0.5" color="#F5C84A" />
        <path d="M 0 550 Q 300 540 600 555 Q 900 570 1200 540" stroke="currentColor" strokeWidth="0.5" color="#F5C84A" />
        <path d="M 0 600 Q 400 590 800 605 Q 1000 612 1200 598" stroke="currentColor" strokeWidth="0.4" color="#F5C84A" />
        <path d="M 0 200 Q 350 185 700 200 Q 1000 215 1200 190" stroke="currentColor" strokeWidth="0.4" color="#F5C84A" />
        <ellipse cx="600" cy="400" rx="500" ry="250" stroke="currentColor" strokeWidth="0.5" color="#F5C84A" />
        <ellipse cx="600" cy="400" rx="380" ry="185" stroke="currentColor" strokeWidth="0.4" color="#F5C84A" />
        <ellipse cx="600" cy="400" rx="260" ry="120" stroke="currentColor" strokeWidth="0.35" color="#F5C84A" />
      </svg>
    </div>
  );
}

function LandingPage() {
  usePageMeta(
    "The IMPACT System™ — Most district initiatives don't fail. They stall.",
    "Free diagnostic for superintendents and district leaders. 34 questions. 12 minutes. Find out exactly where your initiative is most at risk — before you launch."
  );
  return (
    <div className="landing-page" data-testid="landing-page">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <TopoBg />
      <div className="page">
        <nav className="landing-nav" data-testid="landing-nav">
          <Link to="/" className="nav-brand" data-testid="nav-brand-link">
            <div className="nav-mark"><span>I</span></div>
            <div className="nav-wordmark">
              <span className="impact">IMPACT</span><span className="sys"> SYSTEM&trade;</span>
            </div>
          </Link>
          <div className="nav-url">getimpactsystem.com</div>
        </nav>

        <main id="main-content" className="landing-main">
          <div className="content">
            <div className="eyebrow a1" data-testid="hero-eyebrow">For Superintendents &amp; District Leaders</div>
            <h1 className="a2" data-testid="hero-headline">
              Most innovations don&rsquo;t fail. <em>They stall.</em>
            </h1>
            <p className="sub a3" data-testid="hero-subtext">
              Not because the idea was wrong. Because the launch strategy was. The gap between a good initiative and one that actually survives contact with your district is almost always visible before you launch &mdash; if you know where to look.
            </p>
            <div className="proof-list a4" data-testid="proof-list">
              <div className="proof-item">
                <div className="proof-dot"></div>
                <span><strong>What happens at month four</strong> &mdash; when the initial energy is gone and the hard work of getting everyone to actually change their practice begins?</span>
              </div>
              <div className="proof-item">
                <div className="proof-dot"></div>
                <span><strong>Have you named the people who carry the room</strong> &mdash; the ones whose concerns show up in board meetings without their name attached &mdash; and do you have a strategy for each of them?</span>
              </div>
              <div className="proof-item">
                <div className="proof-dot"></div>
                <span><strong>When members of your leadership team describe this initiative to a board member, a teacher, or a parent</strong> &mdash; are they describing the same initiative?</span>
              </div>
            </div>
            <div className="content-divider a5"></div>
            <div className="author-credit a6" data-testid="author-credit">
              <span className="author-name">Matthew Callison, PhD</span>
              <span className="author-sep">&middot;</span>
              <span className="author-title">Director of Innovation &amp; Strategic Partnerships, South Fayette Township School District</span>
            </div>
          </div>

          <div className="form-panel">
            <div className="form-header a3">
              <div className="form-label" data-testid="form-label">Free &middot; 8 Minutes &middot; Results to Your Inbox</div>
              <h2 className="form-headline" data-testid="form-headline">
                Find out where your initiative is most <em>at risk.</em>
              </h2>
              <p className="form-desc">
                34 questions across six areas. Your report &mdash; specific to your situation &mdash; and the Innovation Readiness Guide delivered to your inbox the moment you finish.
              </p>
            </div>
            <div className="assess-entry a4">
              <Link to="/assess" className="assess-hero-btn" data-testid="assess-cta-button">
                Take the Free Assessment &rarr;
              </Link>
              <p className="guide-fallback">
                Not ready yet? <Link to="/guide" className="guide-link">Get the one-page guide first &rarr;</Link>
              </p>
            </div>
            <p className="pricing-context a5">
              The diagnostic is free. Working sessions with your leadership team start at $750.
            </p>
            <p className="privacy-note">
              <svg className="privacy-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              No account required. Your email is used only to send your results and the guide.
            </p>
            <div className="what-you-get a6" data-testid="what-you-get">
              <div className="wyg-label">What you receive when you finish</div>
              <div className="wyg-list">
                <div className="wyg-item"><div className="wyg-check">{'\u2713'}</div>Your report &mdash; what your gaps are, in plain language, specific to your district</div>
                <div className="wyg-item"><div className="wyg-check">{'\u2713'}</div>The Innovation Readiness Guide (PDF) &mdash; five questions every leader should answer before launching</div>
                <div className="wyg-item"><div className="wyg-check">{'\u2713'}</div>A link to talk through your results directly with Matt</div>
              </div>
            </div>
          </div>
        </main>

        <section className="discover-section" aria-labelledby="discover-heading" data-testid="discover-section">
          <div className="discover-inner">
            <div className="discover-eyebrow">What This Diagnostic Actually Measures</div>
            <h2 className="discover-headline" id="discover-heading">
              The gaps that stall district initiatives are almost always visible <em>before launch.</em> Most leaders just don&rsquo;t know where to look.
            </h2>
            <p className="discover-sub">
              The problem isn&rsquo;t communication. It isn&rsquo;t resistance. It isn&rsquo;t even resources. The initiatives that stall do so because the thinking wasn&rsquo;t finished before the launch began &mdash; and nobody had a way to know that until it cost them a year of staff goodwill and board confidence. This is the diagnostic that surfaces those gaps before launch. Eight minutes. Six areas. A clear view of where your thinking is solid and where it isn&rsquo;t.
            </p>
            <div className="discover-dims">
              {[
                { num: "01", name: "Is the thinking actually finished?", desc: "Most leaders have a cloud of intentions, prior conversations, and aspirations that feel like clarity — but haven't been forced into a specific, verifiable statement. Until your initiative can be described in one sentence that every member of your leadership team would write the same way, the thinking isn't done." },
                { num: "02", name: "Who actually decides whether this gets adopted?", desc: "The people who formally approve your initiative are rarely the people who determine whether it survives. The ones who shape adoption are the respected skeptic in the room, the senior staff member everyone watches, the community voice the board trusts. Most leaders don't have a strategy for these people until one of them surfaces at the wrong moment." },
                { num: "03", name: "What forces are working against you that you haven't named yet?", desc: "Budget cycles, personnel changes, competing district priorities, and board dynamics are not disruptions to your initiative — they're the environment it has to survive in. Leaders who identify these forces before launch can plan for them. Leaders who don't get surprised by them six months in." },
                { num: "04", name: "Is your plan specific enough to guide decisions you're not in the room for?", desc: "A plan that doesn't translate to daily decisions isn't a plan — it's a document. The real test: could your assistant superintendent run this initiative next week without losing the intent? If the answer is no, the plan isn't finished." },
                { num: "05", name: "What happens at month four — when the initial energy is gone?", desc: "Most launch plans end at the launch. Almost nothing is designed for the moment four months in when the initial momentum has faded and the hard work of getting people to actually change what they do every day begins. That moment has to be planned for before you launch — not discovered after." },
                { num: "06", name: "Is it safe in your organization to say this isn't working?", desc: "The initiatives that survive aren't the ones with no problems. They're the ones where problems surface early enough to do something about them. That requires a culture and a process — not just good intentions — and both have to exist before the initiative goes public." },
              ].map(d => (
                <div className="discover-dim" key={d.num}>
                  <div className="discover-dim-num">{d.num}</div>
                  <div className="discover-dim-body">
                    <div className="discover-dim-name">{d.name}</div>
                    <div className="discover-dim-desc">{d.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="discover-cta-row">
              <Link to="/assess" className="discover-cta-btn" data-testid="discover-cta-button">Find Out Where You Stand &rarr;</Link>
              <span className="discover-cta-meta">Free &middot; 8 minutes &middot; Results go directly to your inbox</span>
            </div>
          </div>
        </section>

        <footer className="landing-footer a7" data-testid="landing-footer">
          <div className="footer-copy">&copy; 2026 Ember Impact Ventures LLC &middot; Matthew Callison, PhD</div>
          <div className="footer-links">
            <a href="https://getimpactsystem.com">Full Website</a>
            <Link to="/assess">Free Assessment</Link>
            <a href="mailto:matt@emberimpact.com">Contact</a>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;
